import type { IPartialLangDict, ITranslation } from "@/queries/useTranslations";
import { getSearchString } from "@/state/searchCache";
import { type IContentType, type ILanguage, LANGUAGES, PAGE_SIZE, REFERENCE_LANGUAGE } from "@/types";
import type { VersionSearch } from "@/urlState";

export type FilteredIds = {
    texts: string[];
    docs: string[];
    defaultContent: string[];
};

export function getModule(id: string): string {
    const dotIndex = id.indexOf(".");
    if (dotIndex === -1) {
        return id;
    }
    return id.substring(0, dotIndex);
}

export function getAllModules(translations: ITranslation): string[] {
    const modules = new Set<string>();
    for (const id of Object.keys(translations.texts.dict)) {
        modules.add(getModule(id));
    }
    for (const id of Object.keys(translations.docs.dict)) {
        modules.add(getModule(id));
    }
    for (const id of Object.keys(translations.defaultContent.dict)) {
        modules.add(getModule(id));
    }
    return Array.from(modules).sort();
}

export function getModulesForContent(translations: ITranslation, contentType: IContentType): string[] {
    const dict =
        contentType === "texts"
            ? translations.texts.dict
            : contentType === "docs"
              ? translations.docs.dict
              : translations.defaultContent.dict;
    const modules = new Set<string>();
    for (const id of Object.keys(dict)) {
        modules.add(getModule(id));
    }
    return Array.from(modules).sort();
}

function applyIdFilter(ids: string[], idFilter: string | undefined): string[] {
    if (!idFilter) {
        return ids;
    }
    const needle = idFilter.trim().toLowerCase();
    if (!needle) {
        return ids;
    }
    return ids.filter((id) => id.toLowerCase().includes(needle));
}

function applyQueryFilter(
    contentType: IContentType,
    ids: string[],
    dict: Record<string, Record<ILanguage, string>>,
    contexts: Record<string, string>,
    edits: IPartialLangDict | undefined,
    query: string | undefined,
    langs: ILanguage[],
): string[] {
    if (!query) {
        return ids;
    }
    const needle = query.trim().toLowerCase();
    if (!needle) {
        return ids;
    }
    return ids.filter((id) => {
        const contentEdits = edits?.[id];
        const context = contexts[id] ?? "";
        return getSearchString(contentType, id, dict[id], langs, contentEdits, context).includes(needle);
    });
}

function applySinceFilter(
    ids: string[],
    dict: Record<string, Record<ILanguage, string>>,
    compareDict: Record<string, Record<ILanguage, string>> | undefined,
): string[] {
    if (!compareDict) {
        return ids;
    }
    return ids.filter((id) => {
        if (!!dict[id] && !compareDict[id]) {
            return true;
        }
        const val = dict[id][REFERENCE_LANGUAGE]?.trim();
        const ref = compareDict[id]?.[REFERENCE_LANGUAGE]?.trim();
        return val !== ref;
    });
}

function applyEmptyFilter(
    ids: string[],
    dict: Record<string, Record<ILanguage, string>>,
    edits: IPartialLangDict | undefined,
    emptyLangs: ILanguage[] | undefined,
): string[] {
    if (!emptyLangs || emptyLangs.length === 0) {
        return ids;
    }
    return ids.filter((id) => {
        return emptyLangs.some((lang) => {
            const val = edits?.[id]?.[lang] ?? dict[id][lang];
            return !val;
        });
    });
}

function applyModsAllowlist(ids: string[], mods: string[] | undefined): string[] {
    if (!mods) {
        return ids;
    }
    const set = new Set(mods);
    return ids.filter((id) => set.has(getModule(id)));
}

export function filterIdsForContent(
    versionTranslations: ITranslation,
    compareTranslations: ITranslation | undefined,
    editsByContent: { [P in IContentType]?: IPartialLangDict } | undefined,
    contentType: IContentType,
    search: VersionSearch,
): string[] {
    const { ids, dict, contexts } =
        contentType === "texts"
            ? { ...versionTranslations.texts, contexts: versionTranslations.contexts.texts }
            : contentType === "docs"
              ? { ...versionTranslations.docs, contexts: versionTranslations.contexts.docs }
              : { ...versionTranslations.defaultContent, contexts: versionTranslations.contexts.defaultContent };

    const edits = editsByContent?.[contentType];
    const langs = search.langs ?? LANGUAGES;
    const compareDict =
        search.since && compareTranslations
            ? contentType === "texts"
                ? compareTranslations.texts.dict
                : contentType === "docs"
                  ? compareTranslations.docs.dict
                  : compareTranslations.defaultContent.dict
            : undefined;

    let out = ids;
    out = applyIdFilter(out, search.id);
    out = applyQueryFilter(contentType, out, dict, contexts, edits, search.q, langs);
    if (search.since) {
        out = applySinceFilter(out, dict, compareDict);
    }
    out = applyEmptyFilter(out, dict, edits, search.empty);
    out = applyModsAllowlist(out, search.mods);
    return out;
}

export function filterAllIds(
    versionTranslations: ITranslation,
    compareTranslations: ITranslation | undefined,
    editsByContent: { [P in IContentType]?: IPartialLangDict } | undefined,
    search: VersionSearch,
): FilteredIds {
    return {
        texts: filterIdsForContent(versionTranslations, compareTranslations, editsByContent, "texts", search),
        docs: filterIdsForContent(versionTranslations, compareTranslations, editsByContent, "docs", search),
        defaultContent: filterIdsForContent(
            versionTranslations,
            compareTranslations,
            editsByContent,
            "defaultContent",
            search,
        ),
    };
}

export function paginate(ids: string[], page: number): { ids: string[]; pageCount: number } {
    const pageCount = Math.ceil(ids.length / PAGE_SIZE);
    const start = (page - 1) * PAGE_SIZE;
    return { pageCount, ids: ids.slice(start, start + PAGE_SIZE) };
}

export function buildExportData(args: {
    translations: ITranslation;
    editsByContent: { [P in IContentType]?: IPartialLangDict } | undefined;
    filtered: FilteredIds;
    lang: ILanguage;
}): Record<string, string> {
    const { translations, editsByContent, filtered, lang } = args;
    const out: Record<string, string> = {};

    for (const id of filtered.texts) {
        out[`texts.${id}`] = editsByContent?.texts?.[id]?.[lang] ?? translations.texts.dict[id][lang];
    }
    for (const id of filtered.docs) {
        out[`docs.${id}`] = editsByContent?.docs?.[id]?.[lang] ?? translations.docs.dict[id][lang];
    }
    for (const id of filtered.defaultContent) {
        out[`default-content.${id}`] =
            editsByContent?.defaultContent?.[id]?.[lang] ?? translations.defaultContent.dict[id][lang];
    }

    return out;
}
